<script setup lang="ts">
import { defineProps, ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { useNotification } from './composables/useNotification'
import type { Props } from './types'
import { useRoute } from '#app/composables/router'
import ANotificationSwitch from '@/components/atoms/ANotificationSwitch/index.vue'
import ANotificationPanel from '@/components/organisms/ANotificationPanel/index.vue'
import { SideMenuDisplayMode } from '@/constants/enums/SideMenuDisplayMode'
import { throttle } from '@/utils/Form'
import { DisplayMode } from '@/constants/enums/NotificationPanelDisplayMode'

const props = defineProps<Props>()

/**
 * Data
 */
const isMounted = ref(false)
const isPanelOpened = ref(false)

const {
  displayMode,
  isUnreadOnlyDisplay,
  unreadCount,
  notificationData,
  getNotifications,
  changeDisplayMode,
  toggleUnreadOnlyDisplay,
} = useNotification(props)

/**
 * Composables
 */
const route = useRoute()

/**
 * Methods
 */
const resetPanel = () => {
  isPanelOpened.value = false
  // 通知パネルが閉じるアニメーションが終わってから実行する
  setTimeout(() => {
    changeDisplayMode(DisplayMode.LIST)
    isUnreadOnlyDisplay.value = false
  }, 300)
}
const togglePanel = () => {
  isPanelOpened.value = !isPanelOpened.value
  if (!isPanelOpened.value) {
    resetPanel()
  }
  else {
    getNotifications()
  }
}

/**
 * Constants
 */
const GET_NOTIFICATIONS_INTERVAL = 300000 as const // 5分

/**
 * Ref
 */
const interval = ref<number | null>(null)

/**
 * Computed
 */
const panelStyle = computed<{
  minWidth: string
  position: string
  top: string
}>(() => {
  return {
    minWidth: isPanelOpened.value ? '350px' : '0',
    position: 'sticky',
    top: props.sideMenuDisplayMode === SideMenuDisplayMode.SWITCH
      ? '60px'
      : '0',
  }
})
// 通知パネルをヘッダー直下に固定するようにtopを調整しつつ、スクロールで見えなくなった場合はtopを0にする
const handleScroll = throttle(() => {
  const scrollY = window.scrollY
  const offset = Math.max(0, 60 - scrollY)
  const aside = document.getElementById('fix-aside')
  if (aside) {
    aside.style.transform = `translateY(${offset}px)`
  }
}, 100)

/**
 * Watches
 */
watch(() => route.path, () => {
  if (isPanelOpened.value) {
    resetPanel()
  }
})

/**
 * Lifecycle
 */
onMounted(() => {
  isMounted.value = true
  window.addEventListener('scroll', handleScroll)

  interval.value = window.setInterval(() => {
    getNotifications()
  }, GET_NOTIFICATIONS_INTERVAL)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)

  if (interval.value) {
    window.clearInterval(interval.value)
  }
})
</script>

<template>
  <div class="notification-container">
    <ANotificationSwitch
      v-if="!props.isGuest"
      :is-panel-opened="isPanelOpened"
      :count="unreadCount"
      @toggle-panel="togglePanel"
    />
    <Teleport
      v-if="isMounted"
      to="#fix-aside"
    >
      <ANotificationPanel
        v-if="notificationData?.notifications"
        :style="panelStyle"
        :is-opened="isPanelOpened"
        :notification-meta-list="notificationData.notifications"
        :unread-count="unreadCount"
        :display-mode="displayMode"
        :is-unread-only-display="isUnreadOnlyDisplay"
        @refresh="getNotifications"
        @close="togglePanel"
        @change-display-mode="changeDisplayMode"
        @toggle-unread-only-display="toggleUnreadOnlyDisplay"
      />
    </Teleport>
  </div>
</template>
